import { ReactNode, useEffect, useState } from "react";
import { Box, Chip, Grid, Stack, Typography, alpha } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import dayjs from "dayjs";

import { OrdenTrabajo } from "@/shared/interfaces";

const ACCENT = "#1565c0";

function fmt(v?: string | null): string {
  return v ? dayjs(v).format("DD/MM/YYYY HH:mm:ss") : "—";
}

/** Formatea milisegundos a "Xh Ym Zs". */
function fmtDur(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function Campo({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <Grid item xs={6} sm={3}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value ?? "—"}
      </Typography>
    </Grid>
  );
}

type Props = { ot?: OrdenTrabajo; title?: string };

/**
 * Tiempos del técnico EN TIEMPO REAL: inicio, fin y "tiempo tardado". Mientras la
 * visita está en curso (hay inicio pero no fin) muestra un CRONÓMETRO en vivo que
 * corre segundo a segundo; al finalizar fija la duración (fin - inicio). El
 * refresco de inicio/fin depende de que el contenedor recargue la OT (poll): en
 * Calidad y Activaciones se hace cada pocos segundos.
 */
export default function TiemposVisita({ ot, title = "Tiempos del técnico" }: Props) {
  const inicio = (ot as any)?.fecha_hora_inicio_visita as string | undefined;
  const fin = (ot as any)?.fecha_hora_fin_visita as string | undefined;
  const enCurso = !!inicio && !fin;
  const finalizada = !!inicio && !!fin;

  // Cronómetro en vivo solo mientras la visita está en curso.
  const [ahora, setAhora] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!enCurso) return;
    setAhora(Date.now());
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [enCurso, inicio]);

  let duracionMs: number | null = null;
  if (finalizada) duracionMs = dayjs(fin).diff(dayjs(inicio));
  else if (enCurso) duracionMs = ahora - dayjs(inicio).valueOf();
  const duracionLabel = duracionMs != null ? fmtDur(duracionMs) : "—";

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25, flexWrap: "wrap", gap: 0.5 }}>
        <AccessTimeIcon fontSize="small" sx={{ color: ACCENT }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        {enCurso && (
          <Chip size="small" color="warning" label="En curso" sx={{ height: 20, fontSize: 11 }} />
        )}
        {finalizada && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label="Finalizada"
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
      </Stack>

      <Grid container spacing={1.5} alignItems="flex-start">
        <Campo label="Inicio de visita" value={fmt(inicio)} />
        <Campo label="Fin de visita" value={enCurso ? "En curso…" : fmt(fin)} />
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">
            {enCurso ? "Tiempo transcurrido" : "Tiempo tardado"}
          </Typography>
          <Box sx={{ mt: 0.25 }}>
            <Chip
              size="small"
              label={duracionLabel}
              sx={{
                bgcolor: alpha(ACCENT, enCurso ? 0.18 : 0.1),
                color: ACCENT,
                fontWeight: 800,
                fontVariantNumeric: "tabular-nums",
              }}
            />
          </Box>
        </Grid>
        <Campo label="Flota" value={(ot as any)?.flota_data?.nombre || (ot as any)?.flota} />
      </Grid>

      {!inicio && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          La visita aún no ha sido iniciada por el técnico.
        </Typography>
      )}
    </Box>
  );
}
